//propertyType isn't explicitly used in Company level specialty assignments,
//but is required for the form submission to successfully create/update records.
//This might change in the future if we choose to remove property type from Specialty
import React, { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Select from 'react-select';

interface BrokerSpecialty {
  id: number;
  title: string;
  propertyType: string;
}

// Represents the shape expected by react-select component (value/label format)
// Transformed from BrokerSpecialty for use in the dropdown and component state
interface SpecialtyOption {
  value: number;
  label: string;
}

// Maps specialty IDs to their property types for efficient lookup
// Used to populate hidden form fields and track property type selections
// Key: broker specialty ID, Value: object containing property type values
interface SelectedOptionsById {
  [key: number]: {
    values: (string | number)[];
  };
}

interface CompanySpecialty {
  id: number;
  brokerSpecialtyId: number;
  propertyType: string;
}

export interface SpecialtiesProps {
  allBrokerSpecialties: BrokerSpecialty[];
  companySpecialties: CompanySpecialty[];
}

function getPropertyType(propertyTypes: (string | number)[]): string | number {
  return propertyTypes[0];
}

function initialSpecialtiesSelected(
  specialtiesOptions: SpecialtyOption[],
  companySpecialties: CompanySpecialty[]
): SpecialtyOption[] {
  const selectedBrokerSpecialtyId = companySpecialties.map(s => s.brokerSpecialtyId);
  return specialtiesOptions.filter(o => selectedBrokerSpecialtyId.includes(o.value));
}

export default function Specialties({
  allBrokerSpecialties,
  companySpecialties
}: SpecialtiesProps) {
  const specialtiesOptions = allBrokerSpecialties.map(a => ({ value: a.id, label: a.title }));
  const [selectedSpecialties, setSelectedSpecialties] = useState<SpecialtyOption[]>(
    () => initialSpecialtiesSelected(specialtiesOptions, companySpecialties)
  );

  const selectedOptionsById: SelectedOptionsById = selectedSpecialties.reduce<SelectedOptionsById>((accumulator, currentValue) => {
    const specialty = allBrokerSpecialties.find(s => s.id === currentValue.value);
    const propertyType = specialty?.propertyType || '';
    accumulator[currentValue.value] = { values: [propertyType] };
    return accumulator;
  }, {});

  const persistedBrokerSpecialties = companySpecialties.map(s => s.brokerSpecialtyId);
  const newSpecialtyIds = Object.keys(selectedOptionsById).filter(s => (
    !persistedBrokerSpecialties.includes(Number(s))
  ));

  return (
    <>
      <h3 className="top-buffer-lg">Specialties</h3>
      <Row className="form-group">
        <Col md={6}>
          <Select
            classNamePrefix="react-select-buildout"
            isMulti
            options={specialtiesOptions}
            value={selectedSpecialties}
            onChange={(value) => setSelectedSpecialties(value as SpecialtyOption[])}
          />
        </Col>
      </Row>

      {companySpecialties.map((companySpecialty, index) => {
        const specialtyObject = selectedOptionsById[companySpecialty.brokerSpecialtyId];

        return (
          <React.Fragment key={companySpecialty.id}>
            <input name={`company[company_broker_specialties_attributes][${index}][id]`} type="hidden" value={companySpecialty.id}/>
            <input
              name={`company[company_broker_specialties_attributes][${index}][broker_specialty_id]`}
              type="hidden"
              value={companySpecialty.brokerSpecialtyId}
            />
            <input
              name={`company[company_broker_specialties_attributes][${index}][property_type]`}
              type="hidden"
              value={specialtyObject?.values ? getPropertyType(specialtyObject.values) : ''}
            />
            <input
              name={`company[company_broker_specialties_attributes][${index}][_destroy]`}
              type="hidden"
              value={!specialtyObject ? 'true' : 'false'}
            />
          </React.Fragment>
        );
      })}
      
      {newSpecialtyIds.map((specialtyId, i) => {
        const index = i + companySpecialties.length;
        const specialtyObject = selectedOptionsById[Number(specialtyId)];

        return (
          <React.Fragment key={specialtyId}>
            <input
              name={`company[company_broker_specialties_attributes][${index}][broker_specialty_id]`}
              type="hidden"
              value={specialtyId}
            />
            <input
              name={`company[company_broker_specialties_attributes][${index}][property_type]`}
              type="hidden"
              value={getPropertyType(specialtyObject.values)}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}
